import { useCallback, useEffect, useState } from "react";
import {
  ListTree, RefreshCw, CheckCircle2, AlertTriangle, Clock,
  Play, Square, Loader2, RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "./adminUtils";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  const d = Math.floor(diff / 86400);
  return `hace ${d} día${d === 1 ? "" : "s"}`;
}

function StatusPill({ status }) {
  const MAP = {
    running: {
      cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      icon: Play, label: "Running",
    },
    done: {
      cls: "bg-sky-500/15 text-sky-400 border-sky-500/30",
      icon: CheckCircle2, label: "Done",
    },
    pending: {
      cls: "bg-slate-500/15 text-slate-400 border-slate-500/30",
      icon: Clock, label: "Pending",
    },
    failed: {
      cls: "bg-red-500/15 text-red-400 border-red-500/30",
      icon: AlertTriangle, label: "Failed",
    },
    cancelled: {
      cls: "bg-amber-500/15 text-amber-400 border-amber-500/30",
      icon: Square, label: "Cancelled",
    },
  };
  const entry = MAP[status] || MAP.pending;
  const Icon = entry.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${entry.cls}`}>
      <Icon className="w-2.5 h-2.5" />
      {entry.label}
    </span>
  );
}

export default function AdminTasks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [restartingId, setRestartingId] = useState(null);

  const load = useCallback(async (showToast = false) => {
    if (showToast) setRefreshing(true);
    try {
      const r = await adminFetch("/api/admin/tasks");
      setData(r);
      if (showToast) toast.success("Tareas actualizadas");
    } catch (err) {
      toast.error(err.message || "Error cargando tareas");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRestart = async (task) => {
    const label = task.name;
    if (!window.confirm(`¿Reiniciar la tarea «${label}»? Se cancelará la instancia actual y se arrancará una nueva.`)) {
      return;
    }
    setRestartingId(task.name);
    try {
      const r = await adminFetch(`/api/admin/tasks/${encodeURIComponent(task.name)}/restart`, {
        method: "POST",
      });
      toast.success(`«${label}» reiniciada`, {
        description: `Estado previo: ${r.previous_status}`,
      });
      await load();
    } catch (err) {
      toast.error(err.message || "No se pudo reiniciar");
    } finally {
      setRestartingId(null);
    }
  };

  const tasks = data?.tasks || [];
  const oneshots = tasks.filter((t) => t.mode === "oneshot");
  const loops = tasks.filter((t) => t.mode === "loop");
  const failed = tasks.filter((t) => t.status === "failed");

  if (loading) {
    return (
      <div className="p-6 lg:p-8" data-testid="admin-tasks-loading">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-cyan-500/15 rounded-xl flex items-center justify-center">
            <ListTree className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Background Tasks</h1>
            <p className="text-muted-foreground text-sm">Cargando tareas registradas…</p>
          </div>
        </div>
        <div className="grid gap-3">
          {[1,2,3,4].map((i) => (
            <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4 h-20 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const Group = ({ title, rows, description, restartable = false }) => (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h2 className="text-white font-semibold text-sm uppercase tracking-wide">
          {title}
        </h2>
        <span className="text-muted-foreground text-xs">({rows.length})</span>
        {description && (
          <span className="text-muted-foreground text-xs">· {description}</span>
        )}
      </div>
      <div className="rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-muted-foreground text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Nombre</th>
              <th className="px-3 py-2 text-left font-medium">Descripción</th>
              <th className="px-3 py-2 text-left font-medium">Estado</th>
              <th className="px-3 py-2 text-left font-medium">Iniciado</th>
              {restartable && (
                <th className="px-3 py-2 text-right font-medium w-24">Acciones</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((t) => (
              <tr key={t.name}
                  className="bg-slate-950/40 hover:bg-slate-900/40 transition-colors"
                  data-testid={`task-row-${t.name}`}>
                <td className="px-3 py-2">
                  <div className="font-mono text-cyan-300 text-xs font-medium">
                    {t.name}
                  </div>
                  {t.error && (
                    <div className="text-[10px] text-red-400 mt-0.5 truncate max-w-xs"
                         title={t.error}>
                      {t.error}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-400 text-xs">
                  {t.description || "—"}
                </td>
                <td className="px-3 py-2">
                  <StatusPill status={t.status} />
                </td>
                <td className="px-3 py-2 text-muted-foreground text-xs">
                  {t.started_at ? timeAgo(t.started_at) : "—"}
                </td>
                {restartable && (
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleRestart(t)}
                      disabled={restartingId === t.name}
                      data-testid={`task-restart-${t.name}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-slate-300 border border-slate-700 rounded-md hover:bg-slate-800 hover:text-cyan-300 hover:border-cyan-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {restartingId === t.name ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RotateCw className="w-3 h-3" />
                      )}
                      Restart
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={restartable ? 5 : 4} className="px-3 py-4 text-center text-muted-foreground text-xs">
                  Nada registrado aquí.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-tasks-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-500/15 rounded-xl flex items-center justify-center">
            <ListTree className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Background Tasks</h1>
            <p className="text-muted-foreground text-sm">
              {tasks.length} tareas registradas · {loops.filter(t => t.status === "running").length} corriendo · {failed.length > 0 && <span className="text-red-400">{failed.length} fallidas</span>}
            </p>
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="tasks-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <RefreshCw className="w-3 h-3" />}
          Refrescar
        </button>
      </div>

      {/* Failure banner if any */}
      {failed.length > 0 && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-red-300 font-semibold text-sm">
              {failed.length} tarea{failed.length === 1 ? "" : "s"} con errores
            </div>
            <ul className="text-red-400/80 text-xs mt-1 space-y-0.5">
              {failed.map((f) => (
                <li key={f.name} className="font-mono">• {f.name}: {f.error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Group
        title="One-shots"
        description="Ejecutadas al arrancar (indexes, seeds, migraciones)"
        rows={oneshots}
      />
      <Group
        title="Scheduler loops"
        description="Corren en background de forma continua"
        rows={loops}
        restartable
      />
    </div>
  );
}
